import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Subscription } from '@rails/actioncable';
import { useQueryClient } from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdJobsJobIdInvoicing,
  getV1CompaniesCompanyIdOrdersId,
  InvoiceState,
  Order_Read,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import ExpandedStateButton from '~components/ExpandedStateButton/ExpandedStateButton';
import SendTextDialog from '~components/Job/SendTextDialog';
import {
  LoadingSpinner,
  SmallButton,
} from '~components/Order/ordersDispatchStyledComponents';
import Tabs, { TabProps } from '~components/Tabs/Tabs';
import { FetchReason } from '~constants/enums';
import { JobInvoicing } from '~hooks/useJob/models';
import useBulkJobCardPdfs from '~hooks/useJobCardPdf/useBulkJobCardPdfs';
import { Order } from '~hooks/useOrders/models';
import useOrdersDispatch from '~hooks/useOrders/useOrdersDispatch';
import CloneOrderDialog from '~pages/Dispatch/components/CloneOrderDialog';
import { MODAL_DIALOG_ANIMATION_DURATION } from '~pages/Dispatch/components/constants';
import OrderForm from '~pages/Dispatch/components/OrderForm';
import connection from '~services/connectionModule';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { subscribeToChannel } from '~utils/rtu/rtu-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import QuickFilter from '../ApproveJobs/components/QuickFilter';
import BulkCreateSettlementsMenu from '../DriverPayV2/components/CTAs/BulkCreateSettlementsMenu';
import BulkActionsBar from './components/BulkActionsBar/BulkActionsBar';
import BulkDownloadFilesCTA from './components/CTAs/BulkDownloadFilesCTA';
import ExportCsvCTA from './components/CTAs/ExportCsvCTA';
import OrderSelectorCTA from './components/CTAs/OrderSelectorCTA';
import RefreshCTA from './components/CTAs/RefreshCTA';
import DownloadFilesDialog from './components/DownloadFilesDialog/DownloadFilesDialog';
import DateRangeFilter from './components/Filters/DateRangeFilter';
import OrdersFilters from './components/Filters/OrdersFilters';
import Search from './components/Filters/Search';
import OrdersList from './components/OrdersList/OrdersList';
import { GridContainer, GridRow } from './components/styled';
import { ORDERS_LIST_CONTAINER_VERTICAL_GAP } from './constants';
import FilesOrder from './FilesOrder';
import FilesPageContext, {
  DefaultSelectedTab,
  FilesPageContextProvider,
} from './FilesPageContext';
import FilesQueryContext, { FilesQueryContextProvider } from './FilesQueryContext';
import FilesSingleJob from './FilesSingleJob';
import { TicketImportAction } from './TicketImportAction';
import { makeFilesQueryContextKey } from './utils';

type OrderFormState = {
  confirmationDialog: { isCopyAndEdit?: boolean; isOpen: boolean; order?: Order };
  drawer: {
    isCopyAndEdit?: boolean;
    isMinimized: boolean;
    isOpen: boolean;
    keepMounted?: boolean;
    order?: Order;
  };
};

const CLOSED_ORDER_FORM_STATE: OrderFormState = {
  confirmationDialog: { isOpen: false },
  drawer: { isMinimized: false, isOpen: false },
};

enum OrderActionDialog {
  CANCEL_ORDER = 'cancel_order',
  CLONE_ORDER = 'clone_order',
  TEXT_ALL_DRIVERS = 'text_all_drivers',
}

type OrderActionDialogState = { dialog: OrderActionDialog; order: Order };

type DownloadFilesDialogState = { isOpen: boolean; jobIds: string[] };

const CLOSED_DOWNLOAD_FILES_DIALOG_STATE: DownloadFilesDialogState = {
  isOpen: false,
  jobIds: [],
};

export default observer(function FilesContainer() {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [singleJobData, setSingleJobData] = useState<{
    order: Order;
    job: JobInvoicing;
  }>();

  useEffect(() => {
    if (jobId) {
      getV1CompaniesCompanyIdJobsJobIdInvoicing({
        path: { 'company-id': companyId, 'job-id': jobId },
      })
        .then((jobResponse) => {
          const parsedJob = JobInvoicing.parse(jobResponse.data.data);

          getV1CompaniesCompanyIdOrdersId({
            path: { 'company-id': companyId, id: parsedJob.order?.id ?? '' },
          })
            .then((orderResponse) => {
              const parsedOrder = Order.parse(orderResponse.data.data);

              setSingleJobData({ order: parsedOrder, job: parsedJob });
            })
            .catch((error) => {
              navigate('/');
              connection.handleRequestError(
                error,
                t('error_messages.orders.failed_to_fetch_order'),
              );
            });
        })
        .catch((error) => {
          navigate('/');
          connection.handleRequestError(
            error,
            t('error_messages.jobs.failed_to_fetch_individual_job'),
          );
        });
    }
  }, [jobId]);

  if (jobId) {
    return (
      <GridContainer variant="single-job">
        <GridRow
          sx={(theme) => ({
            bgcolor: 'transparent',
            borderTopColor: theme.brandV2.colors.treadGray7,
            overflow: 'auto',
            py: ORDERS_LIST_CONTAINER_VERTICAL_GAP,
          })}
        >
          <LoadingSpinner isVisible={!singleJobData} />

          {singleJobData && (
            <>
              <FilesOrder order={singleJobData.order} />
              <FilesSingleJob order={singleJobData.order} job={singleJobData.job} />
            </>
          )}
        </GridRow>
      </GridContainer>
    );
  }

  return (
    <FilesQueryContextProvider companyId={companyId}>
      <FilesPageContextProvider>
        <Files />
      </FilesPageContextProvider>
    </FilesQueryContextProvider>
  );
});

const Files = observer(() => {
  const filesQuery = useContext(FilesQueryContext);
  const filesPage = useContext(FilesPageContext);
  const { toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const queryClient = useQueryClient();
  const { generateJobCardPdfsForJobs } = useBulkJobCardPdfs();
  const { cancelOrder } = useOrdersDispatch();
  const [orderFormState, setOrderFormState] = useState(CLOSED_ORDER_FORM_STATE);
  const [orderFormSessionKey, setOrderFormSessionKey] = useState(0);
  const [orderActionDialog, setOrderActionDialog] = useState<OrderActionDialogState>();
  const [isCancellingOrder, setIsCancellingOrder] = useState(false);
  const isCancellingOrderRef = useRef(false);
  const [downloadFilesDialog, setDownloadFilesDialog] = useState(
    CLOSED_DOWNLOAD_FILES_DIALOG_STATE,
  );

  const handleTicketImportComplete = useCallback(() => {
    filesQuery.refresh();
  }, [filesQuery.refresh]);

  const [ordersRTUSubscription, setOrdersRTUSubscription] = useState<Subscription>();

  const replaceOrderInQueryCache = useCallback(
    (updatedOrder: Order) => {
      queryClient.setQueriesData<{ pages: Array<Order[]> | undefined }>(
        { queryKey: [makeFilesQueryContextKey()] },
        (data) => {
          if (data?.pages) {
            const newPages = data.pages.map((page) => {
              const cachedOrderIndex = page.findIndex((order) => {
                return order.id === updatedOrder.id;
              });

              if (cachedOrderIndex >= 0) {
                const newPage = [...page];
                newPage.splice(cachedOrderIndex, 1, updatedOrder);

                return newPage;
              }

              return page;
            });

            return { ...data, pages: newPages };
          }

          return data;
        },
      );
    },
    [queryClient],
  );

  const subscribeToOrdersRTU = useCallback(() => {
    subscribeToChannel(
      realTimeChannels.CompanyOrderUpdateChannel,
      companyId,
      setOrdersRTUSubscription,
      ({ data }: { data: Order_Read }) => {
        replaceOrderInQueryCache(Order.parse(data));
      },
    );
  }, [companyId, replaceOrderInQueryCache]);

  const handleEditOrder = useCallback((order: Order) => {
    setOrderFormState((state) => {
      if (!state.drawer.isMinimized) {
        return { ...state, drawer: { isMinimized: false, isOpen: true, order } };
      }

      const isResumingMinimizedOrder = state.drawer.order?.id === order.id;

      if (isResumingMinimizedOrder) {
        return { ...state, drawer: { ...state.drawer, isMinimized: false } };
      }

      return { ...state, confirmationDialog: { isOpen: true, order } };
    });
  }, []);

  const handleCopyAndEditOrder = useCallback((order: Order) => {
    setOrderFormState((state) => {
      if (!state.drawer.isMinimized) {
        return {
          ...state,
          drawer: { isCopyAndEdit: true, isMinimized: false, isOpen: true, order },
        };
      }

      return {
        ...state,
        confirmationDialog: { isCopyAndEdit: true, isOpen: true, order },
      };
    });
  }, []);

  const closeOrderForm = useCallback(() => {
    setOrderFormState(CLOSED_ORDER_FORM_STATE);
  }, []);

  const minimizeOrderForm = useCallback(() => {
    setOrderFormState((state) => ({
      ...state,
      drawer: { ...state.drawer, isMinimized: true, keepMounted: true },
    }));
  }, []);

  const closeConfirmationDialog = useCallback(() => {
    setOrderFormState((state) => ({
      ...state,
      confirmationDialog: { ...state.confirmationDialog, isOpen: false },
    }));
  }, []);

  const discardMinimizedOrderAndEditConfirmedOrder = useCallback(() => {
    setOrderFormSessionKey((sessionKey) => sessionKey + 1);
    setOrderFormState((state) => ({
      confirmationDialog: { ...state.confirmationDialog, isOpen: false },
      drawer: { isMinimized: false, isOpen: false },
    }));

    setTimeout(() => {
      setOrderFormState((state) => ({
        ...state,
        drawer: {
          isCopyAndEdit: state.confirmationDialog.isCopyAndEdit,
          isMinimized: false,
          isOpen: true,
          order: state.confirmationDialog.order,
        },
      }));
    }, MODAL_DIALOG_ANIMATION_DURATION);
  }, []);

  const openCancelOrderDialog = useCallback((order: Order) => {
    setOrderActionDialog({ dialog: OrderActionDialog.CANCEL_ORDER, order });
  }, []);

  const openCloneOrderDialog = useCallback((order: Order) => {
    setOrderActionDialog({ dialog: OrderActionDialog.CLONE_ORDER, order });
  }, []);

  const openTextAllDriversDialog = useCallback((order: Order) => {
    setOrderActionDialog({ dialog: OrderActionDialog.TEXT_ALL_DRIVERS, order });
  }, []);

  const closeOrderActionDialog = useCallback(() => {
    setOrderActionDialog(undefined);
  }, []);

  const closeCancelOrderDialog = useCallback(() => {
    if (isCancellingOrderRef.current) {
      return;
    }

    closeOrderActionDialog();
  }, [closeOrderActionDialog]);

  const confirmCancelOrder = useCallback(async () => {
    const orderToCancel = orderActionDialog?.order;

    if (!orderToCancel || isCancellingOrderRef.current) {
      return;
    }

    isCancellingOrderRef.current = true;
    setIsCancellingOrder(true);

    try {
      const cancelledOrder = await cancelOrder(orderToCancel.id);
      const isEditingCancelledOrder =
        !orderFormState.drawer.isCopyAndEdit &&
        orderFormState.drawer.order?.id === cancelledOrder.id;

      replaceOrderInQueryCache(cancelledOrder);

      if (isEditingCancelledOrder) {
        setOrderFormSessionKey((sessionKey) => sessionKey + 1);
        setOrderFormState(CLOSED_ORDER_FORM_STATE);
      }

      toasterStore.push(
        alert(
          t('dispatch.order.canceled', { name: cancelledOrder.orderId }),
          AlertTypes.success,
        ),
      );
    } catch {
      // cancelOrder has already toasted and reported the failure.
    } finally {
      isCancellingOrderRef.current = false;
      setIsCancellingOrder(false);
      closeOrderActionDialog();
    }
  }, [
    cancelOrder,
    closeOrderActionDialog,
    orderActionDialog?.order,
    orderFormState.drawer.isCopyAndEdit,
    orderFormState.drawer.order?.id,
    replaceOrderInQueryCache,
    toasterStore,
  ]);

  const handleOrderCloned = useCallback(
    (clonedOrder: Order) => {
      toasterStore.push(
        alert(
          t('dispatch.order.created', { name: clonedOrder.orderId }),
          AlertTypes.success,
        ),
      );
      closeOrderActionDialog();
    },
    [closeOrderActionDialog, toasterStore],
  );

  const openDownloadFilesDialog = useCallback((jobIds: string[]) => {
    setDownloadFilesDialog({ isOpen: true, jobIds });
  }, []);

  const closeDownloadFilesDialog = useCallback(() => {
    setDownloadFilesDialog(CLOSED_DOWNLOAD_FILES_DIALOG_STATE);
  }, []);

  useEffect(() => {
    if (filesQuery.fetchReason === FetchReason.INITIAL_FETCH) {
      filesPage.reset();
    }
  }, [filesQuery.fetchReason]);

  useEffect(() => {
    return () => {
      filesQuery.refresh();
    };
  }, [companyId]);

  useEffect(() => {
    return () => {
      filesQuery.clearQueryData();
    };
  }, []);

  useEffect(() => {
    if (!ordersRTUSubscription) {
      subscribeToOrdersRTU();
    }
    return () => {
      ordersRTUSubscription?.unsubscribe();
    };
  }, [ordersRTUSubscription, subscribeToOrdersRTU]);

  return (
    <>
      <GridContainer>
        <GridRow sx={{ mt: '-1px' }}>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1,
              justifyContent: 'space-between',
            }}
          >
            <DateRangeFilter />

            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
              <Tooltip title={t('approvals.files.default_selected_tab_toggle.label')}>
                <span>
                  <DefaultSelectedTabToggle />
                </span>
              </Tooltip>
              <ExpandedStateButton
                onChange={filesPage.toggleAllOrderCardsExpandedState}
                state={filesPage.orderCardsExpandedState.allCards}
              />
              <OrderSelectorCTA />
            </Box>
          </Box>
        </GridRow>

        <GridRow>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <Box sx={{ minWidth: '180px', flex: 0 }}>
              <Search />
            </Box>

            <Box sx={{ flex: 1, display: 'flex', gap: 1 }}>
              <OrdersFilters />

              <QuickFilter
                label={t('dispatch.dispatch_v2.filters.unapproved_payables')}
                filterKey="payablesStates"
                filterValue={[InvoiceState.PENDING, InvoiceState.CUSTOMER_PENDING]}
              />
              <QuickFilter
                label={t('dispatch.dispatch_v2.filters.unapproved_receivables')}
                filterKey="receivablesStates"
                filterValue={[InvoiceState.PENDING, InvoiceState.CUSTOMER_PENDING]}
              />
              {filesPage.defaultSelectedTab === DefaultSelectedTab.JOBS && (
                <QuickFilter
                  label={t('dispatch.dispatch_v2.filters.unapproved_jobs')}
                  filterKey="jobApprovalStatus"
                  filterValue={['false']}
                />
              )}
              {filesPage.defaultSelectedTab === DefaultSelectedTab.LOADS && (
                <QuickFilter
                  label={t('dispatch.dispatch_v2.filters.unapproved_loads')}
                  filterKey="loadApprovalStatus"
                  filterValue={['false']}
                />
              )}
              <QuickFilter
                filterKey="ticketPotentialDuplicate"
                filterValue={['true']}
                label={t('dispatch.dispatch_v2.filters.duplicate_tickets')}
                onActivate={() => {
                  filesPage.setDefaultSelectedTab(DefaultSelectedTab.LOADS);
                }}
              />
            </Box>

            <ExportCsvCTA />
            <TicketImportAction onImportComplete={handleTicketImportComplete} />
            <RefreshCTA />
            <BulkDownloadFilesCTA />

            <BulkCreateSettlementsMenu
              endDate={filesQuery.filters.endDate}
              startDate={filesQuery.filters.startDate}
            />
          </Box>
        </GridRow>

        <GridRow
          sx={(theme) => ({
            bgcolor: 'transparent',
            borderTopColor: theme.brandV2.colors.treadGray7,
            overflow: 'hidden',
            px: 0,
            py: 0,
          })}
        >
          {filesQuery.fetchReason === FetchReason.INITIAL_FETCH ? (
            <LoadingSpinner isVisible sx={{ my: 1.5 }} />
          ) : (
            <OrdersList
              editedOrderId={orderFormState.drawer.order?.id}
              hasMore={filesQuery.hasMore}
              hasUnfilteredResults={filesQuery.hasUnfilteredResults}
              isLoading={filesQuery.fetchReason === FetchReason.INFINITE_SCROLL}
              onCancelOrder={openCancelOrderDialog}
              onClearFilters={filesQuery.clearAllFiltersExceptSearch}
              onCloneOrder={openCloneOrderDialog}
              onCopyAndEditOrder={handleCopyAndEditOrder}
              onDownloadFiles={openDownloadFilesDialog}
              onDownloadJobCardPdfs={generateJobCardPdfsForJobs}
              onEditOrder={handleEditOrder}
              onLoadMore={filesQuery.fetchMore}
              onTextAllDrivers={openTextAllDriversDialog}
              orders={filesQuery.data}
            />
          )}
        </GridRow>
      </GridContainer>

      <BulkActionsBar />

      <OrderForm
        key={orderFormSessionKey}
        disableDispatchDeepLink
        isCopyAndEdit={orderFormState.drawer.isCopyAndEdit}
        isMinimized={orderFormState.drawer.isMinimized}
        isOpen={orderFormState.drawer.isOpen}
        keepMounted={orderFormState.drawer.keepMounted}
        onClose={closeOrderForm}
        onMinimize={minimizeOrderForm}
        onOrderSaved={replaceOrderInQueryCache}
        order={orderFormState.drawer.order}
      />

      <Dialog
        isOpen={orderFormState.confirmationDialog.isOpen}
        maxWidth="sm"
        onClose={closeConfirmationDialog}
      >
        <DialogHeader onClose={closeConfirmationDialog}>
          {t('dispatch.order.edit_order')}
        </DialogHeader>

        <DialogContent>
          <Typography>
            {t('dispatch.dispatch_v2.edit_confirmation_dialog_text', {
              action: orderFormState.drawer.isCopyAndEdit
                ? t('common.created').toLowerCase()
                : t('common.edited').toLowerCase(),
              orderId: orderFormState.confirmationDialog.order?.orderId,
            })}
          </Typography>
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            onClick={closeConfirmationDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallButton
            color="primary"
            onClick={discardMinimizedOrderAndEditConfirmedOrder}
            variant="contained"
          >
            {t('actions.confirm')}
          </SmallButton>
        </DialogActions>
      </Dialog>

      {orderActionDialog?.dialog === OrderActionDialog.CLONE_ORDER && (
        <CloneOrderDialog
          isOpen
          onClose={closeOrderActionDialog}
          onOrderCloned={handleOrderCloned}
          order={orderActionDialog.order}
        />
      )}

      <SendTextDialog
        isOpen={orderActionDialog?.dialog === OrderActionDialog.TEXT_ALL_DRIVERS}
        mode="order_drivers"
        onClose={closeOrderActionDialog}
        source={orderActionDialog?.order}
      />

      <ConfirmationDialog
        isBusy={isCancellingOrder}
        isOpen={orderActionDialog?.dialog === OrderActionDialog.CANCEL_ORDER}
        onClose={closeCancelOrderDialog}
        onConfirm={confirmCancelOrder}
        title={t('dispatch.order.cancel_order')}
      >
        <Typography>{t('dispatch.order.cancel_description')}</Typography>
      </ConfirmationDialog>

      <DownloadFilesDialog
        isOpen={downloadFilesDialog.isOpen}
        jobIds={downloadFilesDialog.jobIds}
        onClose={closeDownloadFilesDialog}
      />
    </>
  );
});

const DEFAULT_SELECTED_TAB_TABS: TabProps<`${DefaultSelectedTab}`>[] = [
  { label: t('form_fields.jobs'), value: DefaultSelectedTab.JOBS },
  { label: t('common.loads'), value: DefaultSelectedTab.LOADS },
];

function DefaultSelectedTabToggle() {
  const { defaultSelectedTab, setDefaultSelectedTab } = useContext(FilesPageContext);

  const handleChange = useCallback(
    (selected: `${DefaultSelectedTab}`) => {
      setDefaultSelectedTab(selected);
    },
    [setDefaultSelectedTab],
  );

  return (
    <Tabs
      onChange={handleChange}
      selected={defaultSelectedTab}
      tabs={DEFAULT_SELECTED_TAB_TABS}
    />
  );
}
